import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  GlassCard,
  Button,
  Tabs,
  SearchInput,
  FilterSelect,
  StatusPill,
  ProgressBar,
  Pagination,
  IconButton,
  DataTable,
  Icon,
} from '../../campaign-os';
import { CampaignDetail } from './CampaignDetail';
import { usePageChrome } from '../PageChrome';
import { useWizard } from '../store/wizardStore';

import { useTableControls } from '../useTableControls';
import { useApiData } from '../data/useApiData';
import { mapCampaigns } from '../data/mapCampaign';
import { campaignService } from '../../../../services/apiService';
import { formatShortDate } from '../data/format';

/**
 * Campaigns — every real email campaign from the backend (GET /campaigns: regularization
 * batches and broadcasts sent through the campaign pipeline). There is no demo data on this screen: a row
 * exists only because mail was actually queued. Tabs / search / sort / paging are
 * client-side over the (≤100-row) list; the row → drawer shows live per-campaign detail.
 */
const TYPES = ['All', 'Regularization', 'Broadcast'];
const TYPE_TO_API = { Regularization: 'REGULARIZATION', Broadcast: 'BROADCAST' };
const SEARCH_KEYS = ['name', 'id', 'templateName', 'audience', 'subject', 'owner'];

const TAB_MATCH = {
  all: () => true,
  completed: (c) => c.status === 'completed',
  sending: (c) => c.status === 'running' || c.status === 'scheduled',
  failed: (c) => c.status === 'failed',
};

const EMPTY_LIST_LABEL = 'No campaigns yet — create one with New Campaign, or send a bulk rectification link from Regularization.';

export function CampaignsPage() {
  const { open } = useWizard();
  const location = useLocation();
  // A just-sent campaign navigates here with a fresh token. Navigating to the route you
  // are already on does NOT remount, so without this the list stays stale and the new
  // campaign looks like it vanished.
  const refreshAt = location.state?.refreshAt ?? null;
  const [tab, setTab] = useState('all');
  const [type, setType] = useState('All');
  const [owner, setOwner] = useState('All');
  const [selected, setSelected] = useState(null);

  const api = useApiData(() => campaignService.list({ limit: 100 }), [refreshAt], { cacheKey: 'campaign:list:100' });

  // Land on "All" after a send, so a filter left on a previous tab cannot hide the new row.
  useEffect(() => {
    if (refreshAt) setTab('all');
  }, [refreshAt]);

  usePageChrome(
    () => ({
      title: 'Campaigns',
      subtitle: 'Manage vendor outreach across the portfolio',
      actions: (
        <>
          <Button variant="secondary" icon="refresh" onClick={api.refetch}>
            Refresh
          </Button>
          <Button variant="primary" icon="add" onClick={() => open()}>
            New Campaign
          </Button>
        </>
      ),
    }),
    [open, api.refetch]
  );

  const rows = useMemo(() => mapCampaigns(api.data?.data), [api.data]);

  const owners = useMemo(() => ['All', ...Array.from(new Set(rows.map((c) => c.owner).filter(Boolean)))], [rows]);

  const tabItems = useMemo(() => {
    const count = (key) => rows.filter(TAB_MATCH[key]).length;
    return [
      { key: 'all', label: 'All', count: count('all') },
      { key: 'completed', label: 'Completed', count: count('completed') },
      { key: 'sending', label: 'Sending', count: count('sending') },
      { key: 'failed', label: 'Failed', count: count('failed') },
    ];
  }, [rows]);

  const preFiltered = useMemo(() => {
    const match = TAB_MATCH[tab] || TAB_MATCH.all;
    const apiType = TYPE_TO_API[type];
    return rows.filter((c) => match(c) && (!apiType || c.type === apiType) && (owner === 'All' || c.owner === owner));
  }, [rows, tab, type, owner]);

  const table = useTableControls({
    data: preFiltered,
    pageSize: 8,
    searchKeys: SEARCH_KEYS,
    initialSort: { key: 'created', dir: 'desc' },
  });

  const columns = [
    {
      key: 'name',
      header: 'Campaign',
      sortable: true,
      render: (c) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-cos-ink">{c.name}</span>
          </div>
          <div className="mono text-2xs text-cos-ink-muted">{c.id} · {c.templateName}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (c) => (
        <span title={c.apiStatusLabel}>
          <StatusPill status={c.status} />
        </span>
      ),
    },
    {
      key: 'audienceSize',
      header: 'Recipients',
      sortable: true,
      align: 'right',
      render: (c) => <span className="mono text-cos-ink">{Number(c.audienceSize || 0).toLocaleString('en-US')}</span>,
    },
    { key: 'progress', header: 'Progress', sortable: true, render: (c) => <div className="w-32"><ProgressBar value={c.progress} showLabel /></div> },
    {
      key: 'responseRate',
      header: 'Response',
      sortable: true,
      align: 'right',
      render: (c) => (
        <span className="mono font-semibold text-cos-ink" title={c.responseLabel}>
          {c.responseRate || c.contacted ? `${c.responseRate}%` : '—'}
        </span>
      ),
    },
    {
      key: 'deadline',
      header: 'Link expiry',
      sortable: true,
      align: 'right',
      render: (c) => <span className="mono text-cos-ink-secondary">{c.deadline ? formatShortDate(c.deadline) : '—'}</span>,
    },
  ];

  const initialLoading = api.loading && !api.data;
  const hardError = api.error && !api.data;

  return (
    <>
      {hardError ? (
        <GlassCard className="flex flex-col items-center gap-3 py-12 text-center">
          <Icon name="cloud_off" className="text-[32px] text-cos-status-coral" />
          <div>
            <div className="text-[15px] font-bold text-cos-ink">Couldn&apos;t load campaigns</div>
            <p className="mt-1 text-[13px] text-cos-ink-secondary">{api.error}</p>
          </div>
          <Button variant="secondary" icon="refresh" onClick={api.refetch}>
            Retry
          </Button>
        </GlassCard>
      ) : (
        <GlassCard padded={false} className="overflow-hidden">
          {/* Controls */}
          <div className="flex flex-col gap-3 border-b border-cos-line-subtle px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Tabs items={tabItems} value={tab} onChange={setTab} size="sm" />
              <div className="flex items-center gap-2">
                <FilterSelect label="Type" value={type} options={TYPES} onChange={setType} icon="category" />
                <FilterSelect label="Owner" value={owner} options={owners} onChange={setOwner} icon="person" />
              </div>
            </div>
            <SearchInput value={table.search} onChange={table.setSearch} placeholder="Search campaigns by name, ID, subject, or type…" className="max-w-md" />
          </div>

          {/* Stale-data warning (a refetch failed but we still have the previous list) */}
          {api.error && api.data && (
            <div className="flex items-center gap-2 border-b border-cos-line-subtle bg-cos-status-amber-bg px-5 py-2 text-2xs text-cos-status-amber">
              <Icon name="warning" className="text-[15px]" />
              <span>Refresh failed — showing the last loaded list. {api.error}</span>
            </div>
          )}

          {/* Table */}
          <div className="px-3 py-1">
            {initialLoading ? (
              <div className="animate-pulse space-y-3 px-3 py-6">
                {Array.from({ length: 5 }, (_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-[rgba(70,80,72,0.08)]" />
                ))}
              </div>
            ) : (
              <DataTable
                columns={columns}
                rows={table.rows}
                rowKey={(c) => c.id}
                sort={table.sort}
                onSort={table.toggleSort}
                onRowClick={setSelected}
                emptyLabel={rows.length === 0 ? EMPTY_LIST_LABEL : 'No campaigns match these filters.'}
                rowActions={(c) => (
                  <IconButton icon="visibility" label="View details" size="sm" onClick={(e) => { e.stopPropagation(); setSelected(c); }} />
                )}
              />
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-cos-line-subtle px-5 py-3">
            <Pagination page={table.page} pageSize={table.pageSize} total={table.total} onPageChange={table.setPage} noun="campaigns" />
          </div>
        </GlassCard>
      )}

      <CampaignDetail campaign={selected} onClose={() => setSelected(null)} />
    </>
  );
}

export default CampaignsPage;
